import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { normalizeBranding } from "../utils/adminActions";

const Ctx = createContext({ logos: {}, ready: false, receipt: { width: '80mm', fontScale: 1, mode: 'COMPACT' } });

function isHttp(url) {
  return /^https?:\/\//i.test(String(url || ""));
}

function resolvePathToUrl(pathOrUrl) {
  try {
    if (!pathOrUrl) return null;
    if (isHttp(pathOrUrl)) return String(pathOrUrl);
    // Treat as Firebase Storage path
    const storage = getStorage();
    return getDownloadURL(ref(storage, String(pathOrUrl)));
  } catch {
    return null;
  }
}

async function toDataUrl(url) {
  try {
    const resp = await fetch(url, { cache: "no-cache" });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export function BrandProvider({ children }) {
  const [raw, setRaw] = useState({});
  const [logos, setLogos] = useState({});
  const [printDataUrl, setPrintDataUrl] = useState(null);
  const [ready, setReady] = useState(false);
  const [receipt, setReceipt] = useState({ width: '80mm', fontScale: 1, mode: 'COMPACT' });

  // Subscribe to Firestore branding doc (if present)
  useEffect(() => {
    let unsub = () => {};
    try {
      const ref = doc(db, "settings", "branding");
      unsub = onSnapshot(ref, (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setRaw(data);
        const r = data.receipt || {};
        setReceipt({
          width: typeof r.width === 'string' ? r.width : '80mm',
          fontScale: Number.isFinite(r.fontScale) ? Math.max(0.8, Math.min(1.5, r.fontScale)) : 1,
          mode: r.mode === 'DETAILED' ? 'DETAILED' : 'COMPACT',
        });
      });
    } catch {}
    return () => {
      try { unsub(); } catch {}
    };
  }, []);

  // Resolve raw fields to URLs
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const normalized = normalizeBranding(raw);
        const entries = ["app", "login", "loading", "print"].map(key => [key, normalized[key]]);
        const out = {};
        for (const [key, val] of entries) {
          if (!val) continue;
          const maybeUrl = resolvePathToUrl(val);
          // resolvePathToUrl may return a Promise (for storage path)
          const url = typeof maybeUrl === "string" ? maybeUrl : await maybeUrl;
          if (url) out[key] = url;
        }
        if (!alive) return;
        setLogos(out);
        setPrintDataUrl(null);
        try { if (out.app) localStorage.setItem('brandLogoAppUrl', out.app); } catch {}
        setReady(true);

        // Precompute print data URL and cache (1 day)
        const printUrl = out.print || out.app || "/logo.jpg";
        const cacheKey = `brand_print_dataurl@${printUrl}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached && alive) {
          setPrintDataUrl(cached);
        } else {
          const dataUrl = await toDataUrl(printUrl);
          if (dataUrl && alive) {
            setPrintDataUrl(dataUrl);
            try { localStorage.setItem(cacheKey, dataUrl); } catch {}
          }
        }
      } catch {
        setReady(true);
      }
    })();
    return () => { alive = false; };
  }, [raw]);

  // Compute brand-driven tile colors early and centrally (was in Sidebar before)
  useEffect(() => {
    let cancelled = false;
    const src = (logos && (logos.app || logos.login || logos.loading)) || "/logo.jpg";
    if (!src) return;
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (cancelled) return;
        try {
          const w = 48, h = 48;
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          let r = 0, g = 0, b = 0, count = 0;
          for (let i = 0; i < data.length; i += 4) {
            const a = data[i + 3];
            if (a < 128) continue;
            r += data[i]; g += data[i + 1]; b += data[i + 2];
            count++;
          }
          if (count > 0) {
            r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
          } else { r = 28; g = 72; b = 110; }

          const toHex = (n) => n.toString(16).padStart(2, "0");
          const base = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
          const shade = (hex, amt) => {
            const x = hex.replace("#", "");
            const rr = parseInt(x.slice(0,2),16), gg = parseInt(x.slice(2,4),16), bb = parseInt(x.slice(4,6),16);
            const adj = (v) => Math.max(0, Math.min(255, Math.round(v + (amt * 255))));
            return `#${toHex(adj(rr))}${toHex(adj(gg))}${toHex(adj(bb))}`;
          };
          const tlLight = shade(base, 0.70);
          const tlDark  = shade(base, -0.12);
          const tlLightBorder = shade(tlLight, -0.10);
          const tlDarkBorder  = shade(tlDark,  -0.10);

          // Push into CSS variables
          const root = document.documentElement;
          if (root) {
            root.style.setProperty("--brand-surface-light", tlLight);
            root.style.setProperty("--brand-border-light", tlLightBorder);
            root.style.setProperty("--brand-surface-dark", tlDark);
            root.style.setProperty("--brand-border-dark", tlDarkBorder);
          }
          // Persist so public/index.html can hydrate BEFORE paint next time
          try {
            localStorage.setItem("brandVars", JSON.stringify({
              surfaceLight: tlLight,
              borderLight: tlLightBorder,
              surfaceDark: tlDark,
              borderDark: tlDarkBorder,
            }));
          } catch {}
        } catch (e) { /* ignore */ }
      };
      img.onerror = () => {};
      img.src = src;
      return () => { cancelled = true; };
    } catch {}
  }, [logos]);

  const value = useMemo(() => ({ logos, printDataUrl, ready, receipt }), [logos, printDataUrl, ready, receipt]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBrand() {
  return useContext(Ctx);
}
